import CacheEnum from '@/enums/cacheEnum';
import { useLocalStorage } from '@/hooks/useStorage';
import { useDebounceFn } from 'ahooks';
import { useEffect, useMemo, useState } from 'react';
import { useTheme } from 'next-themes';
import { useTheme as useThemeLocalStorage } from 'ahooks';
import ProjectSetting from '@/setting/projectSetting';

/**
 * 用于检测当前设备是否为移动设备的Hook。
 * 初始时假设为移动设备，随后根据窗口大小判断是否为移动设备。
 *
 * @returns {Object} 返回一个包含isMobile属性的对象，isMobile表示当前设备是否被认为是移动设备。
 */
export default () => {
  const [isMobile, setIsMobile] = useState(true); // 初始状态将设备设置为移动设备
  const { theme } = useTheme();
  // 
  const { theme: saveTheme, themeMode, setThemeMode: setSaveTheme } = useThemeLocalStorage({
    localStorageKey: 'nextTheme',
  });
  // 当组件挂载和卸载时，监听和取消监听窗口大小改变事件
  useEffect(() => {
    // 定义处理窗口大小改变的函数
    const handleResize = () => {
      requestAnimationFrame(() => {
        // 根据窗口宽度判断是否为移动设备
        setIsMobile(window.innerWidth < 640);
      });
    };
    // 添加窗口大小改变事件的监听
    window.addEventListener('resize', handleResize);
    // 首次渲染后立即判断一次设备类型
    handleResize();
    // 组件卸载时移除事件监听
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 红涨绿跌设置
  const [upDownColorConfig, setUpDownColorConfig] = useLocalStorage<
    'upGreen' | 'upRed'
  >({
    key: CacheEnum.APP_UPDOWNCOLOR_CONFIG, // 全部
    defaultValue: 'upGreen', // 绿涨红跌,默认
  });

  //当前涨的颜色 类名 - 文字颜色
  const currentUpClass: 'text-errorColor' | 'text-successColor' =
    useMemo(() => {
      return upDownColorConfig === 'upGreen'
        ? 'text-successColor'
        : 'text-errorColor';
    }, [upDownColorConfig]);

  //当前跌的颜色 类名- 文字颜色
  const currentDownClass: 'text-errorColor' | 'text-successColor' =
    useMemo(() => {
      return upDownColorConfig === 'upGreen'
        ? 'text-errorColor'
        : 'text-successColor';
    }, [upDownColorConfig]);

  //当前涨的颜色 类名 - 文字颜色
  const borderUpClass: 'border-errorColor' | 'border-successColor' =
    useMemo(() => {
      return upDownColorConfig === 'upGreen'
        ? 'border-successColor'
        : 'border-errorColor';
    }, [upDownColorConfig]);

  //当前跌的颜色 类名- 文字颜色
  const borderDownClass: 'border-errorColor' | 'border-successColor' =
    useMemo(() => {
      return upDownColorConfig === 'upGreen'
        ? 'border-errorColor'
        : 'border-successColor';
    }, [upDownColorConfig]);

  //当前涨的颜色 类名 - bg
  const currentUpBgClass: 'bg-errorColor' | 'bg-successColor' = useMemo(() => {
    return upDownColorConfig === 'upGreen'
      ? 'bg-successColor'
      : 'bg-errorColor';
  }, [upDownColorConfig]);

  //当前跌的颜色 类名- bg
  const currentDownBgClass: 'bg-errorColor' | 'bg-successColor' =
    useMemo(() => {
      return upDownColorConfig === 'upGreen'
        ? 'bg-errorColor'
        : 'bg-successColor';
    }, [upDownColorConfig]);

  //当前涨的颜色
  const riseColor = useMemo(() => {
    return upDownColorConfig === 'upGreen' ? 'successColor' : 'errorColor';
  }, [upDownColorConfig]);

  //当前跌的颜色
  const fallColor = useMemo(() => {
    return upDownColorConfig === 'upGreen' ? 'errorColor' : 'successColor';
  }, [upDownColorConfig]);

  //当前涨的颜色
  const riseColorHexadecimal = useMemo(() => {
    return upDownColorConfig === 'upGreen' ? ProjectSetting.riseColor : ProjectSetting.fallColor;
  }, [upDownColorConfig]);

  //当前跌的颜色
  const fallColorHexadecimal = useMemo(() => {
    return upDownColorConfig === 'upGreen' ? ProjectSetting.fallColor : ProjectSetting.riseColor;
  }, [upDownColorConfig]);

  const [AppKey, setAppKey] = useState<any>(1);

  const reLoadApp = () => {
    setAppKey((AppKey: any) => AppKey + 1);
  };

  const [orderKey, setOrderKey] = useState<any>(1);

  const { run: reLoadOrder } = useDebounceFn(
    () => {
      setOrderKey((AppKey: any) => AppKey + 1);
    },
    {
      wait: 100, // 增加防抖 兼容下后端频繁推送
    },
  );

  const isPc = useMemo(() => {
    return !isMobile;
  }, [isMobile]);

  const isDev = process.env.NODE_ENV === 'development';

  //  断网重连（主要是重新获取接口）
  const [layoutKey, setLayoutKey] = useState(0);

  // 当前股票或者合约的行情
  const [localChoiceContractSocket, setLocalChoiceContractSocket] = useLocalStorage<any>({
    key: CacheEnum.APP_CHOICE_CONTRACT_SOCKET,
    defaultValue: null,
  });
  // 滚动锁
  const [lockScroll, setLockScroll] = useState(false); // 滚动锁

  // 系统当前滚动距离
  const [systemScrollTop, setSystemScrollTop] = useState(0);

  return {
    isMobile, // 返回设备是否为移动设备的判断结果
    upDownColorConfig,
    setUpDownColorConfig,
    saveTheme,
    setSaveTheme,
    currentUpClass, // 当前涨的颜色类名
    currentDownClass, // 当前跌的颜色类名
    currentUpBgClass, // 当前涨的背景颜色类名
    currentDownBgClass, // 当前跌的背景颜色类名
    riseColor,
    fallColor,
    AppKey,
    reLoadApp, //可调用刷新app状态，不是页面刷新
    orderKey,
    reLoadOrder,
    isPc,
    isDev,
    layoutKey,
    setLayoutKey,
    setLocalChoiceContractSocket,
    localChoiceContractSocket,
    lockScroll,
    setLockScroll,
    borderUpClass,
    borderDownClass,
    riseColorHexadecimal,
    fallColorHexadecimal,
    systemScrollTop,
    setSystemScrollTop,
  };
};
